import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

const slides = [
  {
    image: 'https://static.readdy.ai/image/aaba9897d2facfbc7f1204630a4017ac/ef5ebcc2b245c9bec32fb8efb13f3ec3.jpeg',
    captionKey: 'achievement.caption',
    descriptionKey: 'achievement.description',
    badge: 'IQ PCC-PYB',
  },
  {
    image: 'https://static.readdy.ai/image/aaba9897d2facfbc7f1204630a4017ac/95b371aee18c0177625777a69a4158ae.jpeg',
    captionKey: 'achievement.caption2',
    descriptionKey: 'achievement.description2',
    badge: 'IQ CERT',
  },
  {
    image: 'https://static.readdy.ai/image/aaba9897d2facfbc7f1204630a4017ac/d6b8053929c161b9e43802ef8e1d4644.jpeg',
    captionKey: 'achievement.caption3',
    descriptionKey: 'achievement.description3',
    badge: 'ULAK',
  },
];

const AchievementSection = () => {
  const { t } = useTranslation();
  const [current, setCurrent] = useState(0);
  const [animating, setAnimating] = useState(false);

  const goTo = useCallback((index: number) => {
    if (animating) return;
    setAnimating(true);
    setCurrent(index);
    setTimeout(() => setAnimating(false), 500);
  }, [animating]);

  const prev = () => goTo((current - 1 + slides.length) % slides.length);
  const next = () => goTo((current + 1) % slides.length);

  useEffect(() => {
    const timer = setInterval(() => {
      goTo((current + 1) % slides.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [current, goTo]);

  const slide = slides[current];

  return (
    <section className="py-20 bg-white">
      <div className="container mx-auto px-4">
        <div className="text-center mb-14">
          <span className="inline-block px-4 py-1.5 bg-teal-50 text-teal-700 rounded-full text-sm font-semibold mb-4 tracking-wide">
            {t('achievement.badge')}
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">
            {t('achievement.title')}
          </h2>
          <p className="text-gray-500 text-base max-w-2xl mx-auto">
            {t('achievement.subtitle')}
          </p>
        </div>

        <div className="relative max-w-5xl mx-auto">
          {/* Slider */}
          <div className="relative rounded-2xl overflow-hidden shadow-2xl group">
            <div className="w-full h-[420px] md:h-[480px]">
              <img
                key={current}
                src={slide.image}
                alt={t(slide.captionKey)}
                title={t(slide.captionKey)}
                className="w-full h-full object-cover object-top transition-opacity duration-500"
                style={{ opacity: animating ? 0.6 : 1 }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
            </div>

            {/* Caption overlay */}
            <div className="absolute bottom-0 left-0 right-0 p-6 md:p-10">
              <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
                <div>
                  <h3 className="text-white text-xl md:text-2xl font-bold mb-2">
                    {t(slide.captionKey)}
                  </h3>
                  <p className="text-gray-200 text-sm md:text-base max-w-lg leading-relaxed">
                    {t(slide.descriptionKey)}
                  </p>
                </div>
                <div className="flex-shrink-0">
                  <span className="inline-flex items-center gap-2 bg-white/15 backdrop-blur-sm border border-white/25 text-white px-5 py-2.5 rounded-full text-sm font-medium whitespace-nowrap">
                    <i className="ri-award-fill text-teal-300"></i>
                    {slide.badge}
                  </span>
                </div>
              </div>
            </div>

            {/* Prev / Next arrows */}
            <button
              onClick={prev}
              className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center bg-black/40 hover:bg-black/60 text-white rounded-full transition-colors duration-200 cursor-pointer"
              aria-label="Önceki"
            >
              <i className="ri-arrow-left-s-line text-xl"></i>
            </button>
            <button
              onClick={next}
              className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center bg-black/40 hover:bg-black/60 text-white rounded-full transition-colors duration-200 cursor-pointer"
              aria-label="Sonraki"
            >
              <i className="ri-arrow-right-s-line text-xl"></i>
            </button>

            {/* Dot indicators */}
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
              {slides.map((_, i) => (
                <button
                  key={i}
                  onClick={() => goTo(i)}
                  className={`w-2.5 h-2.5 rounded-full transition-all duration-300 cursor-pointer ${
                    i === current ? 'bg-teal-400 w-6' : 'bg-white/50 hover:bg-white/80'
                  }`}
                  aria-label={`Slayt ${i + 1}`}
                />
              ))}
            </div>
          </div>

          {/* Stats row */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
            <div className="bg-gray-50 rounded-xl p-5 text-center hover:bg-teal-50 transition-colors duration-300 cursor-default">
              <div className="w-10 h-10 flex items-center justify-center mx-auto mb-3 bg-teal-100 rounded-lg">
                <i className="ri-global-line text-teal-600 text-lg"></i>
              </div>
              <div className="text-2xl font-bold text-gray-900 mb-1">50+</div>
              <div className="text-xs text-gray-500">{t('achievement.stat1')}</div>
            </div>
            <div className="bg-gray-50 rounded-xl p-5 text-center hover:bg-teal-50 transition-colors duration-300 cursor-default">
              <div className="w-10 h-10 flex items-center justify-center mx-auto mb-3 bg-teal-100 rounded-lg">
                <i className="ri-shield-check-line text-teal-600 text-lg"></i>
              </div>
              <div className="text-2xl font-bold text-gray-900 mb-1">1000+</div>
              <div className="text-xs text-gray-500">{t('achievement.stat2')}</div>
            </div>
            <div className="bg-gray-50 rounded-xl p-5 text-center hover:bg-teal-50 transition-colors duration-300 cursor-default">
              <div className="w-10 h-10 flex items-center justify-center mx-auto mb-3 bg-teal-100 rounded-lg">
                <i className="ri-team-line text-teal-600 text-lg"></i>
              </div>
              <div className="text-2xl font-bold text-gray-900 mb-1">25+</div>
              <div className="text-xs text-gray-500">{t('achievement.stat3')}</div>
            </div>
            <div className="bg-gray-50 rounded-xl p-5 text-center hover:bg-teal-50 transition-colors duration-300 cursor-default">
              <div className="w-10 h-10 flex items-center justify-center mx-auto mb-3 bg-teal-100 rounded-lg">
                <i className="ri-star-line text-teal-600 text-lg"></i>
              </div>
              <div className="text-2xl font-bold text-gray-900 mb-1">%99</div>
              <div className="text-xs text-gray-500">{t('achievement.stat4')}</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AchievementSection;
